import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Send, Bell, CheckCircle2, XCircle, AlertTriangle, RefreshCw } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { sendAdminPush, sendTestPush, getPushSystemDiagnostics } from "@/lib/push.functions";

export const Route = createFileRoute("/admin/notifications")({ component: NotificationsAdmin });

type Scope = "all" | "single";

type HistoryItem = {
  id: string; title: string; message: string; target_scope: Scope;
  target_phone: string | null; sent: number; targeted: number; failed: number; at: string;
};

function StatusRow({ ok, label, value }: { ok: boolean | null; label: string; value?: string | null }) {
  const Icon = ok === true ? CheckCircle2 : ok === false ? XCircle : AlertTriangle;
  const color = ok === true ? "text-emerald-600" : ok === false ? "text-red-600" : "text-amber-600";
  return (
    <div className="flex items-start justify-between gap-2 border-b border-border/50 py-1.5 last:border-0">
      <div className="flex items-center gap-2 text-xs font-medium">
        <Icon className={`h-4 w-4 ${color}`} />
        <span>{label}</span>
      </div>
      <div className="max-w-[60%] break-all text-right text-xs text-muted-foreground">{value ?? (ok === true ? "Yes" : ok === false ? "No" : "—")}</div>
    </div>
  );
}

function NotificationsAdmin() {
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [scope, setScope] = useState<Scope>("all");
  const [phone, setPhone] = useState("");
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState<HistoryItem[]>([]);

  const { data: diag, refetch: refetchDiag, isFetching } = useQuery({
    queryKey: ["push_system_diagnostics"],
    queryFn: async () => await getPushSystemDiagnostics(),
    refetchInterval: 20000,
  });

  async function send() {
    const safeTitle = title.trim();
    const safeMessage = message.trim();
    if (!safeTitle || !safeMessage) return toast.error("Title and message required");
    if (scope === "single" && !phone.trim()) return toast.error("Enter the user's mobile number");
    setSending(true);
    try {
      const res = await sendAdminPush({
        data: { title: safeTitle, message: safeMessage, target_scope: scope,
          target_phone: scope === "single" ? phone.trim() : null },
      });
      const sent = Number(res?.sent ?? 0);
      const targeted = Number(res?.targeted ?? 0);
      const failed = Number(res?.failed ?? 0);
      toast.success(`Sent to ${sent}/${targeted} device(s)${failed ? `, ${failed} failed` : ""}`);
      setHistory((h) => [{
        id: crypto.randomUUID(), title: safeTitle, message: safeMessage,
        target_scope: scope, target_phone: scope === "single" ? phone.trim() : null,
        sent, targeted, failed, at: new Date().toISOString(),
      }, ...h].slice(0, 50));
      setTitle(""); setMessage(""); setPhone("");
      refetchDiag();
    } catch (e: any) {
      console.error("[notifications] send failed", e);
      toast.error(e?.message ?? "Failed to send notification");
    } finally { setSending(false); }
  }

  async function test() {
    try {
      const res = await sendTestPush();
      const sent = Number(res?.sent ?? 0);
      const targeted = Number(res?.targeted ?? 0);
      const failed = Number(res?.failed ?? 0);
      toast.success(`Test sent to ${sent}/${targeted} of your device(s)${failed ? `, ${failed} failed` : ""}`);
    } catch (e: any) {
      toast.error(e?.message ?? "Test push failed");
    }
  }

  const fb = diag?.firebase;

  return (
    <div className="space-y-4">
      <h1 className="flex items-center gap-2 text-2xl font-black"><Bell className="h-6 w-6" /> Notifications</h1>

      {/* Diagnostics panel */}
      <div className="rounded-2xl bg-card p-4 shadow-card">
        <div className="mb-2 flex items-center justify-between">
          <h2 className="text-sm font-bold">Push system diagnostics</h2>
          <Button size="sm" variant="ghost" onClick={() => refetchDiag()} disabled={isFetching}>
            <RefreshCw className={`mr-1 h-3.5 w-3.5 ${isFetching ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
        <div className="rounded-lg border bg-muted/20 p-3">
          <StatusRow ok={!!fb?.initialized} label="Firebase server credentials loaded"
            value={fb?.initialized ? (fb?.projectId ?? "yes") : (fb?.serviceAccountConfigured ? "invalid JSON" : "missing FIREBASE_SERVICE_ACCOUNT_JSON")} />
          <StatusRow ok={!!fb?.projectId} label="Firebase project ID" value={fb?.projectId ?? "unknown"} />
          <StatusRow ok={(diag?.totalActiveAndroidDevices ?? 0) > 0}
            label="Active Android devices (all users)"
            value={String(diag?.totalActiveAndroidDevices ?? 0)} />
          <StatusRow ok={(diag?.myActiveDevices ?? 0) > 0}
            label="My admin device tokens"
            value={String(diag?.myActiveDevices ?? 0)} />
          <StatusRow ok={diag?.lastError ? false : null}
            label="Last token-registration error"
            value={diag?.lastError ? `${diag.lastError}${diag.lastErrorAt ? " · " + new Date(diag.lastErrorAt).toLocaleString("en-IN") : ""}` : "none"} />
        </div>

        {(diag?.myActiveDevices ?? 0) > 0 && (
          <div className="mt-2 space-y-1">
            {diag!.myTokens.map((t: any, i: number) => (
              <div key={i} className="rounded border bg-background p-2 text-[11px] text-muted-foreground">
                <span className="font-mono">{t.preview}</span>
                {t.appVersion ? <> · v{t.appVersion}</> : null}
                {t.deviceId ? <> · {t.deviceId.slice(0, 8)}</> : null}
                {t.lastSeenAt ? <> · seen {new Date(t.lastSeenAt).toLocaleString("en-IN")}</> : null}
              </div>
            ))}
          </div>
        )}

        {!fb?.initialized && (
          <div className="mt-3 rounded-lg border border-amber-500/40 bg-amber-500/10 p-2 text-xs text-amber-800">
            Server-side Firebase service account is missing or invalid. Contact the developer to set FIREBASE_SERVICE_ACCOUNT_JSON.
          </div>
        )}
        {fb?.initialized && (diag?.totalActiveAndroidDevices ?? 0) === 0 && (
          <div className="mt-3 rounded-lg border border-amber-500/40 bg-amber-500/10 p-2 text-xs text-amber-800">
            No Android devices have registered an FCM token yet. This almost always means the APK is missing
            <code className="mx-1 rounded bg-black/10 px-1">google-services.json</code>
            or the Firebase package name does not match <code className="mx-1 rounded bg-black/10 px-1">com.brothersclub.app</code>.
            Install the latest APK on a phone, open the app while signed in, grant notification permission, then refresh this page.
          </div>
        )}
      </div>

      {/* Composer */}
      <div className="rounded-2xl bg-card p-4 shadow-card space-y-3">
        <h2 className="text-sm font-bold">Send Android push notification</h2>
        <Input placeholder="Title" value={title} onChange={(e) => setTitle(e.target.value)} />
        <Textarea placeholder="Message" value={message} onChange={(e) => setMessage(e.target.value)} rows={3} />
        <div>
          <label className="mb-1 block text-xs font-semibold">Target</label>
          <Select value={scope} onValueChange={(v) => setScope(v as Scope)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Users</SelectItem>
              <SelectItem value="single">Single User (by mobile)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {scope === "single" && (
          <div>
            <label className="mb-1 block text-xs font-semibold">Mobile number</label>
            <Input type="tel" inputMode="numeric" placeholder="e.g. 9876543210"
              value={phone} onChange={(e) => setPhone(e.target.value)} />
            <p className="mt-1 text-[11px] text-muted-foreground">10-digit Indian number or +91XXXXXXXXXX.</p>
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          <Button onClick={send} disabled={sending} className="gradient-primary shadow-purple">
            <Send className="mr-2 h-4 w-4" /> {sending ? "Sending…" : "Send Notification"}
          </Button>
          <Button variant="outline" onClick={test}>Send Test Notification</Button>
        </div>
      </div>

      {/* Recent sends */}
      <div className="rounded-2xl bg-card p-4 shadow-card">
        <h2 className="mb-3 text-sm font-bold">Recent sends (this session)</h2>
        <div className="space-y-2">
          {history.length === 0 && (
            <p className="py-6 text-center text-sm text-muted-foreground">No notifications sent yet.</p>
          )}
          {history.map((n) => (
            <div key={n.id} className="rounded-xl border p-3">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-sm font-bold">{n.title}</p>
                <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-bold uppercase">
                  {n.target_scope === "single" ? `single · ${n.target_phone}` : "all users"}
                </span>
                <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-bold text-primary">
                  {n.sent}/{n.targeted} delivered{n.failed ? ` · ${n.failed} failed` : ""}
                </span>
              </div>
              <p className="mt-1 text-xs text-muted-foreground">{n.message}</p>
              <p className="mt-1 text-[10px] text-muted-foreground">{new Date(n.at).toLocaleString("en-IN")}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
